import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ColDef } from 'ag-grid-community';
import { flatten, isEqual, uniq, reverse } from 'lodash';
import cytoscape from '../../library/util/cytoscape';
import { IMergeMap } from '../../pages/GraphView/GraphView';
import { expandCandidateSearch } from '../API/candidateApi';
import { createAdvancedQuery, createBatchQuery, getFormBuilderOptions, createQuery, createDslQuery, getSuggestions, getTaxonomyLookupList, getQueryResults, addSearchTag, getQueryList, getExpansionData, deleteSearchTag, getJoinedResults, getGraphPreview, deleteSearch, getSearchStatus, getQueryParameters, saveGraphState, getQueryConstraints, bulkDeleteSearch, uploadSupplementalData, getSupplementalDataInfo, getDashboardData, getATAIResponse, updateRootIds, getChatHistory, disambiguateEntity, getEntitySummaryGraph, getRootIds, elasticSearch } from '../API/queryApi';
import { DslAggregations, DslActiveFacet } from '../../pages/QueryBuilder/dsl/types';
import { normalizeDslAggregations } from '../../pages/QueryBuilder/dsl/aggregations';
import type { AppDispatch, RootState } from '../store/store';
import { mapAttributes, mapNotes, mapFlags } from '../transform/candidateTransform';
import { expandAllCytoscapeData, transformForCytoscape } from '../transform/graphDataTransform';
import { mapQueryListAttributes, createDefaultColumnDefinition, indexQueryResults, getValidDisplayParams, getValueKeys, transformFilterLabelsForDisplay, transformQueryBuilderForDisplay, transformQueryBuilderForQuery, transformSuggestions } from '../transform/queryTransform';
import { ICandidate, setCandidateDetails } from './candidateSlice';


const queryParamsSessionKey = 'queryParamsSessionKey';
const searchPreviewSessionKey = 'searchPreviewSessionKey';
const searchPreviewSelectionSessionKey = 'searchPreviewSelectionSessionKey';

// `queryParamsSessionKey` is only ever meant to hold a classic-mode IQueryBuilder (it needs
// `.filters`, which the rest of QueryBuilder.tsx reads without a null check). A DSL search payload
// has a completely different shape (queryType/queryDepth/dsl, no filters) but flows through the same
// setQueryBuilderRef reducer, so both the read and write sides must validate the shape rather than
// trusting whatever JSON happens to be sitting in localStorage.
export const getPersistedQueryBuilderState = (): IQueryBuilder => {
  try {
    const parsed = JSON.parse(localStorage.getItem(queryParamsSessionKey) || 'null');
    return Array.isArray(parsed?.filters) ? parsed : initialQueryBuilderState;
  } catch {
    return initialQueryBuilderState;
  }
};

export interface SetGraphDataModel extends ICytoscapeDataModel {
  entityId?: string;
  parentMap?: { [child: string]: { id: string, label: string } },
  mergedData?: IMergeMap
  positionData?: { [id: string]: { x: number, y: number }}
}

export interface IQueryBuilder {
  queryType: string,
  queryDepth: number,
  size: number,
  from?: number,
  searchQuery?: string,
  expand: boolean,
  expanded: boolean,
  labels: string[],
  exactMatch: boolean,
  operatorBoolean: boolean,
  operator: string,
  filters: { key: string, constraint: string, value: string }[],
  name?: string
}

export interface IQueryBuilderRef {
  payload: IQueryBuilder,
  response: any
}

export const initialCytoscapeDataModel: ICytoscapeDataModel = {
  nodes: [],
  edges: []
}

export interface ICytoscapeDataModelReference {
  nodes: { [id: string]: cytoscape.ElementDefinition },
  edges: { [id: string]: cytoscape.ElementDefinition }
}

export interface ICytoscapeDataModel {
  nodes: cytoscape.ElementDefinition[],
  edges: cytoscape.ElementDefinition[]
}

export interface IQueryFilter {
  key: string,
  type: string,
  value: string,
  subject: string
}

export const defaultParameterConstraint = 'Equals';

interface QueryState {
  recentHistory: any[];
  queryList: any[];
  taxonomyList: string[];
  suggestionResults: string[];
  previewSearchSelection: string[];
  previewSearchResults: any[];
  searchResults: any[];
  colDef: ColDef[];
  columnOptions: string[];
  graphDataModel: ICytoscapeDataModelReference;
  graphData: ICytoscapeDataModel;
  mergedData: IMergeMap;
  displayName: string;
  rootIds: string[];
  expandedIds: string[];
  excludedIds: string[];
  previewGraphData: ICytoscapeDataModel;
  queryFilters: IQueryFilter[];
  queryBuilder: IQueryBuilder;
  searchExpandReference: boolean;
  queryBuilderListOptions: string[];
  queryConstraintTypes: string[];
  queryBuilderRef: IQueryBuilderRef;
  searchExpandedRef: boolean;
  isCatalogResponse: boolean;
  correlationId: string;
  entityId: string;
  searchMode: 'dsl' | 'classic';
  dslQuery: string;
  dslAggregations: DslAggregations | null;
  dslActiveFacets: DslActiveFacet[];
}

export const initialQueryBuilderState: IQueryBuilder = {
  queryType: 'Person',
  queryDepth: 1,
  size: 50,
  searchQuery: '',
  expand: false,
  expanded: false,
  exactMatch: false,
  operatorBoolean: false,
  operator: 'OR',
  labels: [],
  filters: [{ key: 'Name', constraint: defaultParameterConstraint, value: '' }]
}

export const initialGraphDataModel = {
  nodes: {},
  edges: {}
}

// Define the initial state using that type
const initialState: QueryState = {
  recentHistory: [],
  queryList: [],
  taxonomyList: [],
  suggestionResults: [],
  previewSearchSelection: JSON.parse(localStorage.getItem(searchPreviewSelectionSessionKey)) || [],
  previewSearchResults: JSON.parse(localStorage.getItem(searchPreviewSessionKey)) || [],
  searchResults: [],
  colDef: [],
  columnOptions: [],
  graphDataModel: {
    nodes: {},
    edges: {}
  },
  graphData: {
    nodes: [],
    edges: []
  },
  mergedData: {},
  displayName: '',
  rootIds: [],
  excludedIds: [],
  expandedIds: [],
  previewGraphData: {
    nodes: [],
    edges: []
  },
  queryFilters: [],
  searchExpandReference: false,
  queryBuilder: getPersistedQueryBuilderState(),
  queryBuilderListOptions: [],
  queryConstraintTypes: ['Contains', 'Equals', 'Starts With', 'Ends With'],
  queryBuilderRef: {
    response: undefined,
    payload: getPersistedQueryBuilderState()
  },
  searchExpandedRef: false,
  isCatalogResponse: true,
  correlationId: '',
  entityId: '',
  searchMode: 'classic',
  dslQuery: '',
  dslAggregations: null,
  dslActiveFacets: [],
};

export const callGetRecentHistory =createAsyncThunk(
  'query/getRecentHistory',
  async (args, { dispatch }) => {
    const response = await getQueryList(20);
    dispatch(setRecentHistory(mapQueryListAttributes(response.data)));
    return response;
  }
)

export const callGetQueryList = createAsyncThunk(
  'query/getQueryList',
  async (args: { size?: number, from?: number } = {}, { dispatch }) => {
    const { size = 100, from = 0 } = args;
    const response = await getQueryList(size, from);
    dispatch(setQueryList(mapQueryListAttributes(response.data)));
    return response;
  }
)

export const callGetTaxonomyLookupList = createAsyncThunk(
  'query/getTaxonomyLookupList',
  async (args, { dispatch }) => {
    const response = await getTaxonomyLookupList();
    dispatch(setTaxonomyList(response.data.data));
    return response;
  }
);

export const callGetFormBuilderOptions = createAsyncThunk<any, { taxonomyString: string }, { dispatch: AppDispatch }>(
  'query/getFormBuilderOptions',
  async ({ taxonomyString }, { dispatch }) => {
    const response = await getFormBuilderOptions(taxonomyString);
    const { labels } = transformQueryBuilderForDisplay(response.data);
    dispatch(setQueryBuilderListOptions(labels));
    return response;
  }
);

export const callGetAllFormBuilderOptions = createAsyncThunk<any, void, { dispatch: AppDispatch }>(
  'query/getAllFormBuilderOptions',
  async (args, { dispatch }) => {
    const personResponse = await getFormBuilderOptions('Person');
    const organizationResponse = await getFormBuilderOptions('Organization');
    const thingResponse = await getFormBuilderOptions('Thing');
    
    // Extract and combine labels from each response
    const personLabels = transformQueryBuilderForDisplay(personResponse.data).labels;
    const organizationLabels = transformQueryBuilderForDisplay(organizationResponse.data).labels;
    const thingLabels = transformQueryBuilderForDisplay(thingResponse.data).labels;

    // Combine all labels into one array
    const combinedLabels = uniq([...personLabels, ...organizationLabels, ...thingLabels]);

    return [combinedLabels, personLabels, organizationLabels, thingLabels];
  }
);

export const callGetQueryConstraints = createAsyncThunk(
  'query/getQueryConstraints',
  async (args, { dispatch }) => {
    const { data } = await getQueryConstraints();
    if (data.data?.length) {
      dispatch(setQueryConstraints(transformFilterLabelsForDisplay(reverse(data.data))))
    }
  }
)

export const callGetSuggestions = createAsyncThunk<any, { queryString: string, taxonomyString: string}, {dispatch: AppDispatch}>(
    'query/getSuggestions',
    async ({ queryString, taxonomyString }, { dispatch }) => {
      const response = await getSuggestions(queryString, taxonomyString);
      const returnValue = transformSuggestions(response.data.nodes, queryString);
      dispatch(setQuerySuggestions(returnValue));
      return returnValue;
    }
);

export const callGetQueryParameters = createAsyncThunk<any, { correlationId: string }, { dispatch: AppDispatch }>(
  'query/getQueryParams',
  async ({ correlationId }, { dispatch }) => {
    const response = await getQueryParameters(correlationId);
    dispatch(setSearchExpandedRef(response.data.expanded));
    return response.data;
  }
)

export const callCreateAdvancedQuery = createAsyncThunk(
  'query/createAdvancedQuery',
  async (payload: IQueryBuilder, { dispatch }) => {
    const transformedPayload = transformQueryBuilderForQuery(payload)
    const response = await createAdvancedQuery(transformedPayload);
    dispatch(setCorrelationId(response.data.correlationId));
    return {
      dataModel: handleQueryResponse(payload, response.data, dispatch),
      expanded: response.data?.extended || [],
      excluded: response.data?.excluded || []
    };
  }
)

export const callGetTargetQuery = createAsyncThunk(
  'query/getTargetQuery',
  async (payload: IQueryBuilder, { dispatch }) => {
    const transformedPayload = transformQueryBuilderForQuery(payload)
    const response = await createAdvancedQuery(transformedPayload);
    dispatch(setCorrelationId(response.data.correlationId));
    return response.data;
  }
)

export const callCreateQuery = createAsyncThunk<any, IQueryBuilder, { dispatch: AppDispatch }>(
  'query/createQuery',
  async (payload, { dispatch }) => {
    const response = await createQuery(payload);
    dispatch(setCorrelationId(response.data.correlationId));
    return {
      dataModel: handleQueryResponse(payload, response.data, dispatch),
      expanded: response.data?.extended || [],
      excluded: response.data?.excluded || []
    } 
  }
);

export const callCreateDslQuery = createAsyncThunk<
  any,
  { queryType: string; queryDepth: number; dsl: string; name?: string; size?: number; from?: number; expand?: boolean },
  { dispatch: AppDispatch }
>(
  'query/createDslQuery',
  async (payload, { dispatch }) => {
    const response = await createDslQuery(payload);
    dispatch(setCorrelationId(response.data.correlationId));
    dispatch(setDslAggregations(normalizeDslAggregations(response.data.aggregations)));
    return {
      dataModel: handleQueryResponse(payload, response.data, dispatch),
      expanded: response.data?.extended || [],
      excluded: response.data?.excluded || []
    };
  }
)

export const callCreateBatchQuery = createAsyncThunk(
  'query/createBatchQuery',
  async (payload: { searches: any[] }, { dispatch }) => {
    const response = await createBatchQuery(payload);
    return response.data;
  }
)


export const callUploadSupplementalData = createAsyncThunk(
  'query/uploadSupplementalData',
  async (payload: { file: File }, { dispatch }) => {
    const response = await uploadSupplementalData(payload);
    return response;
  }
)

export const callGetATAIResponse = createAsyncThunk(
  'query/getATAIResponse',
  async (payload: { input: string, type: string }, { dispatch }) => {
    const response = await getATAIResponse(payload);
    return response.data;
  }
)

export const callGetChatHistory = createAsyncThunk(
  'query/getChatHistory',
  async (args, { dispatch }) => {
    const response = await getChatHistory();
    return response.data;
  }
)

export const callElasticSearch = createAsyncThunk(
  'query/elasticSearch',
  async (payload: { indexName: string, mappingContext: Record<string, string> }, { dispatch }) => {
    const response = await elasticSearch(payload);
    return response.data;
  }
)

const handleQueryResponse = (payload: any, data: any, dispatch: AppDispatch) => {
    const { expand, queryType } = payload;
    dispatch(setQueryBuilderRef({
      payload,
      response: data
    }));
    
    const nodes = data.nodes || [];
    const transformedNodes = nodes.every(node => node.attachment) 
      ? nodes.map(node => ({ 
          id: node.id,
          name: node.name, 
          ...node.attachment
        }))
      : nodes;
    
    dispatch(setPreviewSearchResults(indexQueryResults(transformedNodes)));
    const dataKeys = getValueKeys(transformedNodes || []);
    dispatch(setColumnDefinitions(createDefaultColumnDefinition(dataKeys)));
    if (expand) {
      dispatch(callGetQueryList());
    }
  return data || {};
}

export const callGetPreviewData = createAsyncThunk<any, { entityId: string, entityType: string }>(
  'query/getPreviewData',
  async ({entityId, entityType}, { dispatch, signal }) => {
    const response = await getGraphPreview(entityId, entityType, signal);
    const returnValue = (mapAttributes(response.data) as unknown as ICandidate);
    returnValue.notes = mapNotes(response.data.notes || []);
    returnValue.flags = mapFlags(response.data.flags || []);
    dispatch(setCandidateDetails(returnValue));
    return handleGraphResults(response.data, dispatch, entityId);
  }
)

export const callResultsApi = createAsyncThunk(
  'query/getResults',
  async (refId: string) => {
    const response = await getQueryResults(refId);
    return response.data;
  }
)

// const handleQueryResults = (payload: any, dispatch: any) => {
//   if (payload.expanded && !payload?.nodes?.length) {
//       dispatch(setIsCatalogResponse(false))
//       return payload;
//     }
//     dispatch(setQueryFilters(transformFiltersForDisplay(flatten(payload.filters), payload.taxonomyName, payload.searchQuery)))
//     dispatch(setSearchResults(indexQueryResults(payload.nodes, payload?.taxonomyName?.toLowerCase(), payload.rootIds)));
//     dispatch(setIsCatalogResponse(!payload?.edges?.length));
//     const dataKeys = !!payload?.nodes?.length ? getValidDisplayParams(Object.keys(payload.nodes[0])) : [];
//     dataKeys.push('popularityScore');
//     dispatch(setColumnDefinitions(createDefaultColumnDefinition(dataKeys)));
//     dispatch(setColumnOptions(dataKeys));
//     return payload;
// }

export const callExpandCandidateSearch = createAsyncThunk<any, { entityType: string, entityId: string }, { dispatch: AppDispatch, state: RootState }>(
  'query/expandEntity',
  async ({ entityType, entityId }, { dispatch, getState }) => {
    const state = getState();
    const dataModelRef = selectGraphDataModel(state);
    const response = await expandCandidateSearch(entityType, entityId);
    const { data } = transformForCytoscape(response.data);
    const returnValue = expandAllCytoscapeData(data);
    returnValue.nodes = returnValue.nodes.filter((node) => {
      return !dataModelRef.nodes[node.data.id]
    })
    returnValue.edges = returnValue.edges.filter((edge) => {
      return !dataModelRef.edges[edge.data.id]
    });
    dispatch(setGraphDataModel( { nodes: { ...dataModelRef.nodes, ...data.nodes }, edges: { ...dataModelRef.edges, ...data.edges }} ));
    return returnValue;
  }
)

export const callGetExpansionData = createAsyncThunk<any, { entityType: string, entityId: string, mergedData: IMergeMap }, { dispatch: AppDispatch, state: RootState }>(
  'query/getQueryResults',
  async (payload, { dispatch, getState }): Promise<ICytoscapeDataModel> => {
    const state = getState();
    const depth = selectQueryDepth(state);
    const dataModelRef = selectGraphDataModel(state);
    const response = await getExpansionData(payload.entityId, payload.entityType, depth);
    if (response.data) {
      const { data } = transformForCytoscape({ ...response.data, merge: payload.mergedData });
      const returnValue = expandAllCytoscapeData(data);
      returnValue.nodes = returnValue.nodes.filter((node) => {
        return !dataModelRef.nodes[node.data.identity]
      })
      returnValue.edges = returnValue.edges.filter((edge) => {
        return !dataModelRef.edges[edge.data.id]
      });
      const setValue = { nodes: { ...dataModelRef.nodes, ...data.nodes }, edges: { ...dataModelRef.edges, ...data.edges }};
      dispatch(setGraphDataModel(setValue))
      return returnValue;
    } else {
      return { nodes: [], edges: [] };
    }
  }
);

export const handleGraphResults = (payload: any, dispatch: any, entityId: string) => {
  const { data } = transformForCytoscape(payload);
  dispatch(setGraphDataModel(data));
  dispatch(setMergedData(payload.merge));
  dispatch(setExcludedIdsRedux(payload.excluded || []));
  dispatch(setExpandedIdsRedux(payload.extended || []))
  let dataModel: ICytoscapeDataModel = expandAllCytoscapeData(data);
  const nodes: { [id: string]: cytoscape.ElementDefinition } = {};
  const edges: { [id: string]: cytoscape.ElementDefinition } = {};
  dataModel.nodes.forEach((node) => {
    nodes[node.data.id] = node;
    nodes[node.data.entityId] = node;
  })
  dataModel.edges.forEach((edge) => {
    edges[edge.data.id] = edge;
  });
  const dataModelRef: ICytoscapeDataModelReference = {
    nodes,
    edges
  };
  dispatch(setGraphData(dataModel));
  dispatch(setGraphDataModel(dataModelRef));
  return dataModel;
}

export const callGetEntityIdData = createAsyncThunk<any, { entityId: string, from?: string, entityType?: string }>(
  'query/getCorrelationIdData',
  async ({ entityId, from, entityType}, { dispatch }) => {
      if (from === 'summary') {
        const response = await getEntitySummaryGraph(entityId, entityType);
        const returnValue = (mapAttributes({...response?.data?.candidatesAttributes}) as unknown as ICandidate)
        returnValue.notes = mapNotes(response.data.notes || []);
        returnValue.flags = mapFlags(response.data.flags || []);
        dispatch(setCandidateDetails(returnValue));
        dispatch(setRootIds((response.data.data?.rootIds || []).map((rootEle) => rootEle.id)));
        dispatch(setDisplayName(response.data?.displayName || ''));
        dispatch(setQueryFilters(response.data.filters));
        return {
          dataModel: handleGraphResults(response.data, dispatch, entityId),
          expanded: response.data?.extended || [],
          excluded: response.data?.excluded || [],
          correlationId: response.data?.correlationId || ''
        }
      }
      else {
        const response = await dispatch(callResultsApi(entityId));
        const returnValue = (mapAttributes({...response?.payload?.candidatesAttributes}) as unknown as ICandidate)
        returnValue.notes = mapNotes(response.payload.notes || []);
        returnValue.flags = mapFlags(response.payload.flags || []);
        dispatch(setCandidateDetails(returnValue));
        dispatch(setRootIds((response.payload.search?.rootIds || []).map((rootEle) => rootEle.id)));
        dispatch(setDisplayName(response.payload?.search?.displayName || ''));
        dispatch(setQueryFilters(response.payload.search.filters));
        return {
          dataModel: handleGraphResults(response.payload.search, dispatch, entityId),
          expanded: response.payload?.search?.extended || [],
          excluded: response.payload?.search?.excluded || []
        }
      }
  }
)

export const callGetJoinedEntities = createAsyncThunk<any, { selectedNodes: string[], queryForm: IQueryBuilder, name: string }>(
  'query/getCorrelationIdData',
  async ({ selectedNodes, queryForm, name }, { dispatch }) => {
    const { queryDepth, queryType } = queryForm;
    const payload = {
      ids: selectedNodes,
      name,
      operator: queryForm.operator,
      depth: queryDepth,
      searchQuery: queryForm?.searchQuery || '',
      taxonomyType: queryType,
      filters: queryForm?.filters || []
    }
    const response = await getJoinedResults(payload);
    dispatch(setEntityId(response.data.correlationId));
    dispatch(setRootIds((response.data.search?.rootIds || []).map((rootEle) => rootEle.id)));
    handleGraphResults(response.data, dispatch, response.data.correlationId);
    return response.data;
  }
)

export const callAddSearchTag = createAsyncThunk(
  'query/addSearchTag',
  async (payload: any) => {
    if (payload.entityId.indexOf(',') > -1) {
      payload.entityId.split(', ').forEach(async (id) => {
        await deleteSearchTag(payload.searchId, id);
        const newPayload = {
          ...payload,
          entityId: id
        }
        await addSearchTag(newPayload);
      })
    } else {
      await deleteSearchTag(payload.searchId, payload.entityId);
      await addSearchTag(payload);
    }
  }
)

export const callFillQueryForm = createAsyncThunk(
  'query/fillQueryForm',
  async (payload: any, { dispatch }) => {
    if (payload.nodes.length) {
      dispatch(setPreviewSearchResults(indexQueryResults(payload.nodes)));
      const arrayOfKeys: string[][] = payload.nodes.map((rowItem) => Object.keys(rowItem));
      const providedDataKeys = uniq(flatten(arrayOfKeys) || []);
      const dataKeys = !!payload.nodes?.length ? getValidDisplayParams(providedDataKeys) : [];
      if (payload.rootIds?.length) {
        dataKeys.push('popularityScore');
      }
      dispatch(setColumnDefinitions(createDefaultColumnDefinition(dataKeys)));
    }
  }
)

export const callDeleteSearch = createAsyncThunk(
  'query/deleteSearch',
  async (searchId: string, { dispatch }) => {
    await deleteSearch(searchId);
    dispatch(callGetQueryList());
  }
)

export const callBulkDeleteSearch = createAsyncThunk(
  'query/bulkDeleteSearch',
  async (searchIds: string, { dispatch }) => {
    await bulkDeleteSearch(searchIds);
    dispatch(callGetQueryList());
  }
)

export const callGetSearchStatus = createAsyncThunk(
  'query/getSearchStatus',
  async (correlationId: string, { dispatch }) => {
    const response = await getSearchStatus(correlationId);
    return response.data;
  }
);

export const callResetQueryForm = createAsyncThunk(
  'query/resetQueryForm',
  async (payload, { dispatch }) => {
    dispatch(setQueryBuilderRef({
        response: undefined,
        payload: { ...initialQueryBuilderState }
    }));
    dispatch(setEntityId(''));
    dispatch(setSearchExpandedRef(false));
    dispatch(setQueryBuilderParams({ ...initialQueryBuilderState }));
    dispatch(setColumnDefinitions([]));
    dispatch(setPreviewSearchResults([]));
    dispatch(setPreviewSearchSelection([]));
  }
)

export const callSaveGraphState = createAsyncThunk<any, { rootIds: string[], excluded: string[], extended: string[], name: string, correlationId: string }>(
  'query/saveGraphState',
  async (payload, { dispatch }) => {
    const response = await saveGraphState(payload);
    return response.data;
  }
)

export const callUpdateRootIds = createAsyncThunk(
  'query/updateRootIds',
  async (payload: { selectedRows: any[], hasTargets: boolean, rootIds: string[], searchId: string }, { dispatch }) => {
    const response = await updateRootIds(payload);
    return response.data;
  }
)

export const callGetRootIds = createAsyncThunk(
  'query/getRootIds',
  async (payload: { searchIds: string[] }, { dispatch }) => {
    const response = await getRootIds(payload);
    return response;
  }
)

export const callDisambiguateEntity = createAsyncThunk(
  'query/disambiguateEntity',
  async (payload: { searchId: string }, { dispatch }) => {
    const response = await disambiguateEntity(payload);
    return response;
  }
)

export const querySlice = createSlice({
  name: 'query',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setRecentHistory: (state, action: PayloadAction<any[]>) => {
      state.recentHistory = action.payload;
    },
    setQueryList: (state, action: PayloadAction<any[]>) => {
      state.queryList = action.payload;
    },
    setQueryBuilderRef: (state, action: PayloadAction<IQueryBuilderRef>) => {
      // A DSL search's payload (queryType/queryDepth/dsl, no `filters`) flows through this same
      // reducer via handleQueryResponse — only persist payloads that are actually a classic-mode
      // IQueryBuilder, so a DSL search can't overwrite this key (in memory or in localStorage)
      // with a shape the rest of the app (which reads `.filters` unguarded, e.g. MultipleTargets
      // and QueryBuilderOld) doesn't expect.
      const isClassicModePayload = Array.isArray((action.payload.payload as IQueryBuilder | undefined)?.filters);
      if (!isClassicModePayload) {
        return;
      }
      state.queryBuilderRef = action.payload;
      localStorage.setItem(queryParamsSessionKey, JSON.stringify(action.payload.payload));
    },
    setSearchExpandedRef: (state, action: PayloadAction<boolean>) => {
      state.searchExpandedRef = action.payload;
    },
    setTaxonomyList: (state, action: PayloadAction<string[]>) => {
      state.taxonomyList = action.payload;
    },
    setQueryBuilderListOptions: (state, action: PayloadAction<string[]>) => {
      state.queryBuilderListOptions = action.payload;
    },
    setQueryBuilderParams: (state, action: PayloadAction<IQueryBuilder>) => {
      if (isEqual(state.queryBuilder, action.payload)) {
        return;
      }
      state.queryBuilder = action.payload;
    },
    setQueryConstraints: (state, action: PayloadAction<string[]>) => {
      state.queryConstraintTypes = action.payload;
    },
    setQuerySuggestions: (state, action: PayloadAction<string[]>) => {
        state.suggestionResults = action.payload;
    },
    setSearchResults: (state, action: PayloadAction<any[]>) => {
      state.searchResults = action.payload;
    },
    setPreviewSearchSelection: (state, action: PayloadAction<string[]>) => {
      state.previewSearchSelection = action.payload;
      localStorage.setItem(searchPreviewSelectionSessionKey, JSON.stringify(action.payload));
    },
    setPreviewSearchResults: (state, action: PayloadAction<any[]>) => {
      state.previewSearchResults = action.payload;
      localStorage.setItem(searchPreviewSessionKey, JSON.stringify(action.payload));
    },
    setColumnOptions: (state, action: PayloadAction<string[]>) => {
      state.columnOptions = action.payload;
    },
    setColumnDefinitions: (state, action: PayloadAction<ColDef[]>) => {
      state.colDef = action.payload;
    },
    setGraphData: (state, action: PayloadAction<SetGraphDataModel>) => {
      state.graphData = action.payload;
    },
    setMergedData: (state, action: PayloadAction<IMergeMap>) => {
      state.mergedData = action.payload;
    },
    setRootIds: (state, action: PayloadAction<string[]>) => {
      state.rootIds = action.payload;
    },
    setExpandedIdsRedux: (state, action: PayloadAction<string[]>) => {
      state.expandedIds = action.payload;
    },
    setExcludedIdsRedux: (state, action: PayloadAction<string[]>) => {
      state.excludedIds = action.payload;
    },
    setGraphPreviewData: (state, action: PayloadAction<ICytoscapeDataModel>) => {
      state.previewGraphData = action.payload;
    },
    setGraphDataModel: (state, action: PayloadAction<ICytoscapeDataModelReference>) => {
      state.graphDataModel = action.payload;
    },
    setIsCatalogResponse: (state, action: PayloadAction<boolean>) => {
      state.isCatalogResponse = action.payload;
    },
    setCorrelationId: (state, action: PayloadAction<string>) => {
      state.correlationId = action.payload;
    },
    setEntityId: (state, action: PayloadAction<string>) => {
      state.entityId = action.payload;
    },
    setQueryFilters: (state, action: PayloadAction<IQueryFilter[]>) => {
      state.queryFilters = action.payload;
    },
    setDisplayName: (state, action: PayloadAction<string>) => {
      state.displayName = action.payload;
    },
    setSearchMode: (state, action: PayloadAction<'dsl' | 'classic'>) => {
      state.searchMode = action.payload;
    },
    setDslQuery: (state, action: PayloadAction<string>) => {
      state.dslQuery = action.payload;
    },
    setDslAggregations: (state, action: PayloadAction<DslAggregations | null>) => {
      state.dslAggregations = action.payload;
    },
    setDslActiveFacets: (state, action: PayloadAction<DslActiveFacet[]>) => {
      state.dslActiveFacets = action.payload;
    }
  }
});

export const {
  setRecentHistory,
  setQueryList,
  setTaxonomyList,
  setQueryBuilderRef,
  setSearchExpandedRef,
  setQueryConstraints,
  setQueryBuilderListOptions,
  setQueryBuilderParams,
  setQuerySuggestions,
  setSearchResults,
  setPreviewSearchSelection,
  setPreviewSearchResults,
  setColumnOptions,
  setColumnDefinitions,
  setGraphData,
  setMergedData,
  setRootIds,
  setExcludedIdsRedux,
  setExpandedIdsRedux,
  setGraphPreviewData,
  setGraphDataModel,
  setIsCatalogResponse,
  setCorrelationId,
  setEntityId,
  setQueryFilters,
  setDisplayName,
  setSearchMode,
  setDslQuery,
  setDslAggregations,
  setDslActiveFacets
} = querySlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectRecentHistory = (state: RootState) => state.query.recentHistory;
export const selectQueryList = (state: RootState) => state.query.queryList;
export const selectTaxonomyList = (state: RootState) => state.query.taxonomyList;
export const selectQueryBuilder = (state: RootState) => state.query.queryBuilder;
export const selectQueryDepth = (state: RootState) => state.query.queryBuilder?.queryDepth || 1;
export const selectQueryBuilderRef = (state: RootState) => state.query.queryBuilderRef;
export const selectSearchExpandedRef = (state: RootState) => state.query.searchExpandedRef;
export const selectQueryBuilderListOptions = (state: RootState) => state.query.queryBuilderListOptions;
export const selectSortedQueryBuilderListOptions = createSelector(
  selectQueryBuilderListOptions,
  // Case-sensitive lexicographic (ASCII) order is intentional; matches previous inline sort behaviour
  (options) => [...options].sort()
);
export const selectQueryConstraintTypes = (state: RootState) => state.query.queryConstraintTypes;
export const selectQuerySuggestions = (state: RootState) => state.query.suggestionResults;
export const selectSearchResults = (state: RootState) => state.query.searchResults;
export const selectPreviewSearchSelection = (state: RootState) => state.query.previewSearchSelection;
export const selectPreviewSearchResults = (state: RootState) => state.query.previewSearchResults;
export const selectColumnDefinition = (state: RootState): ColDef[] => state.query.colDef;
export const selectColumnOptions = (state: RootState): string[] => state.query.columnOptions;
export const selectGraphDataModel = (state: RootState): ICytoscapeDataModelReference => state.query.graphDataModel;
export const selectIsCatalogResponse = (state: RootState): boolean => state.query.isCatalogResponse;
export const selectCorrelationId = (state: RootState): string => state.query.correlationId;
export const selectEntityId = (state: RootState): string => state.query.entityId;
export const selectQueryFilters = (state: RootState): IQueryFilter[] => state.query.queryFilters;
export const selectGraphData = (state: RootState): ICytoscapeDataModel => state.query.graphData;
export const selectRootIds = (state: RootState): string[] => state.query.rootIds;
export const selectExcludedIds = (state: RootState): string[] => state.query.excludedIds;
export const selectExpandedIds = (state: RootState): string[] => state.query.expandedIds;
export const selectDisplayName = (state: RootState): string => state.query.displayName;
export const selectMergedData = (state: RootState): IMergeMap => state.query.mergedData;
export const selectSearchMode = (state: RootState): 'dsl' | 'classic' => state.query.searchMode;
export const selectDslQuery = (state: RootState): string => state.query.dslQuery;
export const selectDslAggregations = (state: RootState): DslAggregations | null => state.query.dslAggregations;
export const selectDslActiveFacets = (state: RootState): DslActiveFacet[] => state.query.dslActiveFacets;

export default querySlice.reducer;
